// src/components/Login/ResetPasswordBox.js
import React, { useMemo, useState } from 'react';
import './Login.css';

export default function ResetPasswordBox({ onBack }) {
  const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '');
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const checks = useMemo(() => ({
    hasLen: password.length >= 8,
    hasUpper: /[A-Z]/.test(password),
    hasLower: /[a-z]/.test(password),
    hasNum: /\d/.test(password),
  }), [password]);

  const match = password === confirm;
  const formValid = token && Object.values(checks).every(Boolean) && match;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formValid || status === 'submitting') return;

    setStatus('submitting');
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Could not reset password.');
      }

      setStatus('done');
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  if (!token) {
    return (
      <div>
        <p className="error-message">The reset link is missing or invalid.</p>
        <button className="submit-button" onClick={onBack}>Back to Login</button>
      </div>
    );
  }

  if (status === 'done') {
    return (
      <div>
        <p className="success-message">Password updated! You can log in with your new password.</p>
        <button className="submit-button" onClick={onBack}>Back to Login</button>
      </div>
    );
  }

  return (
    <form className="login-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor="new-pass">New Password</label>
        <div className="password-field">
          <input
            id="new-pass"
            type={showPassword ? 'text' : 'password'}
            autoComplete="new-password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={status === 'submitting'}
            style={{ fontSize: '16px' }}
          />
          <button
            type="button"
            className="toggle-password"
            onClick={() => setShowPassword((s) => !s)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            disabled={status === 'submitting'}
          >
            {showPassword ? '🙈' : '👁️'}
          </button>
        </div>

        <ul className="req-list" aria-live="polite">
          <li className={`req-item ${checks.hasLen ? 'valid' : ''}`}>8+ characters</li>
          <li className={`req-item ${checks.hasUpper ? 'valid' : ''}`}>At least 1 uppercase</li>
          <li className={`req-item ${checks.hasLower ? 'valid' : ''}`}>At least 1 lowercase</li>
          <li className={`req-item ${checks.hasNum ? 'valid' : ''}`}>At least 1 number</li>
        </ul>
      </div>

      <div className="form-group">
        <label htmlFor="new-pass-confirm">Confirm Password</label>
        <input
          id="new-pass-confirm"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          disabled={status === 'submitting'}
          style={{ fontSize: '16px' }}
        />
        {!match && confirm.length > 0 && (
          <p className="input-warning">Passwords do not match</p>
        )}
      </div>

      {error && <p className="error-message">{error}</p>}

      <button
        className="submit-button"
        disabled={!formValid || status === 'submitting'}
      >
        {status === 'submitting' ? 'Updating…' : 'Update Password'}
      </button>

      <p style={{ textAlign: 'center', marginTop: '1rem' }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'none',
            border: 'none',
            color: '#3b82f6',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontSize: '0.875rem',
            padding: 0
          }}
        >
          Back to Login
        </button>
      </p>
    </form>
  );
}
